/**
 * Prescrições vazias e a leitura em uma linha.
 *
 * Fica fora dos componentes para poder ser testado sem montar tela — e porque
 * a mesma regra serve a dois lugares: o item novo precisa de um ponto de
 * partida, e a lista precisa resumir o que já existe.
 */

import type { Prescription, PrescriptionType } from './api';
import type { DraftPrescription } from './draft';

/** Os cinco formatos, para a tela oferecer a troca item a item. */
export const PRESCRIPTION_TYPES: ReadonlyArray<PrescriptionType> = [
  'strength',
  'cardio',
  'functional',
  'mobility',
  'swimming',
];

/**
 * A prescrição que combina com a modalidade do modelo.
 *
 * `running_walking` é modalidade e `cardio` é o formato dela — os nomes não
 * batem, e essa é justamente a razão de a tradução existir num lugar só.
 */
export function typeForModality(modality: string): PrescriptionType {
  if (modality === 'running_walking') return 'cardio';
  if (modality === 'functional') return 'functional';
  if (modality === 'mobility') return 'mobility';
  if (modality === 'swimming') return 'swimming';
  return 'strength';
}

/**
 * Um item novo já vem preenchido com valores plausíveis.
 *
 * Campo vazio obrigaria a pessoa a decidir sete coisas antes de ver o item na
 * lista — e o servidor recusaria metade das combinações vazias, porque uma
 * corrida sem tempo nem distância não diz o que fazer.
 */
export function emptyPrescription(type: PrescriptionType): Prescription {
  switch (type) {
    case 'cardio':
      return { type, target_duration_seconds: 1200, intervals: [] };
    case 'functional':
      return { type, rounds: 3, work_seconds: 40, rest_seconds: 20, format: 'interval' };
    case 'mobility':
      return { type, hold_seconds: 30, side: 'both' };
    case 'swimming':
      return { type, pool_length: 25, pool_unit: 'm', target_distance_m: 800, sets: [] };
    default:
      return { type: 'strength', sets: 3, reps_min: 8, reps_max: 12, rest_seconds: 60 };
  }
}

/** O tradutor do i18next, na forma mínima que este módulo usa. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * O resumo que aparece no cartão do item.
 *
 * Existe porque a lista precisa dizer o que o item pede **sem** abrir o
 * formulário — rolar doze itens abertos num celular não é navegar, é procurar.
 *
 * Recebe o tradutor em vez de devolver texto pronto. A primeira versão montava
 * "3 rodadas · 40s" em português dentro da função, o que teria funcionado em
 * português e mentido nos outros dois idiomas — sem erro nenhum, e sem o
 * `i18n:check` perceber, porque não havia chave para conferir.
 *
 * Unidades curtas (`kg`, `m`, `km`, `s`) ficam literais: são símbolos, iguais
 * nos três idiomas, e traduzi-las criaria três chaves para o mesmo caractere.
 */
export function summarize(prescription: DraftPrescription, t: Translate): string {
  // O resumo aparece enquanto a pessoa digita, então precisa saber desenhar o
  // campo ainda vazio. Um traço diz "falta isto" melhor do que "null".
  const numero = (valor: number | null) => (valor === null ? '—' : String(valor));
  const minutos = (segundos: number) =>
    segundos >= 60
      ? t('templates:unit.minutes', { count: Math.round(segundos / 60) })
      : `${segundos}s`;

  switch (prescription.type) {
    case 'strength': {
      const faixa =
        prescription.reps_min === prescription.reps_max
          ? numero(prescription.reps_min)
          : `${numero(prescription.reps_min)}–${numero(prescription.reps_max)}`;
      const carga = prescription.target_load_kg ? ` · ${prescription.target_load_kg} kg` : '';
      const descanso = prescription.rest_seconds ? ` · ${prescription.rest_seconds}s` : '';
      return `${numero(prescription.sets)} × ${faixa}${carga}${descanso}`;
    }
    case 'cardio': {
      const partes: string[] = [];
      if (prescription.target_duration_seconds)
        partes.push(minutos(prescription.target_duration_seconds));
      if (prescription.target_distance_m)
        partes.push(`${(prescription.target_distance_m / 1000).toFixed(1)} km`);
      if (prescription.intervals?.length)
        partes.push(t('templates:unit.intervals', { count: prescription.intervals.length }));
      return partes.join(' · ');
    }
    case 'functional': {
      const trabalho = prescription.work_seconds
        ? `${prescription.work_seconds}s`
        : t('templates:unit.reps', { count: prescription.target_reps ?? 0 });
      const descanso = prescription.rest_seconds ? `/${prescription.rest_seconds}s` : '';
      const rodadas = t('templates:unit.rounds', { count: prescription.rounds ?? 0 });
      return `${rodadas} · ${trabalho}${descanso}`;
    }
    case 'mobility': {
      const partes: string[] = [];
      if (prescription.hold_seconds) partes.push(`${prescription.hold_seconds}s`);
      if (prescription.repetitions)
        partes.push(t('templates:unit.reps', { count: prescription.repetitions }));
      if (prescription.duration_seconds) partes.push(minutos(prescription.duration_seconds));
      return partes.join(' · ');
    }
    case 'swimming': {
      const piscina = `${prescription.pool_length} ${prescription.pool_unit}`;
      const alvo = prescription.target_distance_m ? ` · ${prescription.target_distance_m} m` : '';
      const series = prescription.sets?.length
        ? ` · ${t('templates:unit.swimSets', { count: prescription.sets.length })}`
        : '';
      return `${piscina}${alvo}${series}`;
    }
  }
}
